// @ts-check
// Rotas REST das páginas de agendamento (docs/arquitetura-appcrm/10-contrato-agendamento.md, seção 8).
import { api } from './api-client.js';

/**
 * @typedef {import('../../../shared/booking/booking-values.js').BookingPageValues} BookingPageValues
 * @typedef {BookingPageValues & { id: string, publicId: string, publicUrl: string, apiBaseUrl: string, embedSnippet: string, createdAt: string, updatedAt: string,
 *   hosts: Array<BookingPageValues['hosts'][number] & { position?: number, lastAssignedAt?: string | null }> }} BookingPage
 * @typedef {{ id: string, name: string, eventType: string, durationMinutes: number, isActive: boolean, publicId: string, publicUrl: string,
 *   availabilitySource: string, hostCount: number, upcomingBookingCount: number, updatedAt: string }} BookingPageSummary
 * @typedef {{ connectedAccountId: string, provider: 'GOOGLE' | 'MICROSOFT', handle: string, canCreateEvents: boolean, authFailed: boolean,
 *   isEligible: boolean, reason: string | null }} BookingHostAccount
 * @typedef {{ connectedAccountId: string, provider: string, handle: string, acceptedAt: string | null, canCreateEvents: boolean }} BookingHostConsent
 * @typedef {{ workspaceMemberId: string, name: string, email: string | null, accounts: BookingHostAccount[] }} BookingHost
 * @typedef {{ id: string, name: string, isPrimary: boolean, accessRole: string }} GoogleCalendar
 * @typedef {{ startsAt: string, endsAt: string, hostWorkspaceMemberIds?: string[] }} BookingSlot
 * @typedef {{ timeZone: string, from: string, to: string, days: Array<{ date: string, slots: BookingSlot[] }> }} BookingSlotDays
 * @typedef {{ id: string, status: 'CONFIRMED' | 'CANCELED', startsAt: string, endsAt: string, source: 'PUBLIC' | 'BOT', inviteeName: string | null,
 *   inviteeEmail: string | null, inviteePhone: string | null, hostWorkspaceMemberId: string, hostName: string | null, personId: string | null,
 *   enrollmentOpportunityId: string | null, provider: 'GOOGLE' | 'ICS' | null, providerStatus: string, conferenceLink: string | null,
 *   cancelReason: string | null, answers: Array<{ label: string, value: string }> }} BookingRow
 */

/** @param {any} body @param {string} key */
const readData = (body, key) => body?.data?.[key];

/** @returns {Promise<BookingPageSummary[]>} */
export async function listBookingPages() {
  return readData(await api.get('/rest/booking-pages'), 'bookingPages') ?? [];
}

/** @param {string} bookingPageId @returns {Promise<BookingPage>} */
export async function getBookingPage(bookingPageId) {
  return readData(await api.get(`/rest/booking-pages/${bookingPageId}`), 'bookingPage');
}

/** @param {Record<string, unknown>} input @returns {Promise<BookingPage>} */
export async function createBookingPage(input) {
  return readData(await api.post('/rest/booking-pages', input), 'bookingPage');
}

/** @param {string} bookingPageId @param {Record<string, unknown>} patch @returns {Promise<BookingPage>} */
export async function updateBookingPage(bookingPageId, patch) {
  return readData(await api.patch(`/rest/booking-pages/${bookingPageId}`, patch), 'bookingPage');
}

/** @param {string} bookingPageId */
export function deleteBookingPage(bookingPageId) {
  return api.delete(`/rest/booking-pages/${bookingPageId}`);
}

/** @returns {Promise<BookingHost[]>} */
export async function listBookingHosts() {
  return readData(await api.get('/rest/booking-pages/hosts'), 'hosts') ?? [];
}

/** Contas Google do próprio membro e o aceite de ser anfitrião por cada uma. @returns {Promise<BookingHostConsent[]>} */
export async function listBookingHostConsents() {
  return readData(await api.get('/rest/booking-pages/host-consent'), 'accounts') ?? [];
}

/** @param {string} connectedAccountId @param {boolean} accepted @returns {Promise<BookingHostConsent[]>} */
export async function setBookingHostConsent(connectedAccountId, accepted) {
  return readData(await api.put(`/rest/booking-pages/host-consent/${connectedAccountId}`, { accepted }), 'accounts') ?? [];
}

/**
 * Convida outro membro a aceitar ser anfitrião (aviso no sino e e-mail com o link do aceite).
 * @param {{ workspaceMemberId: string, bookingPageId?: string | null }} input
 * @returns {Promise<{ workspaceMemberId: string, memberName: string, wasCreated: boolean }>}
 */
export async function inviteBookingHost(input) {
  return readData(await api.post('/rest/booking-pages/host-invitations', input), 'invitation');
}

/** @param {string} workspaceMemberId @param {string | null} connectedAccountId @returns {Promise<GoogleCalendar[]>} */
export async function listHostCalendars(workspaceMemberId, connectedAccountId) {
  return readData(await api.get(`/rest/booking-pages/hosts/${workspaceMemberId}/calendars`, { connectedAccountId: connectedAccountId ?? undefined }), 'calendars') ?? [];
}

/** @param {string} bookingPageId @param {{ from?: string, to?: string }} [range] @returns {Promise<BookingSlotDays>} */
export async function listBookingSlots(bookingPageId, range = {}) {
  return (await api.get(`/rest/booking-pages/${bookingPageId}/slots`, range))?.data;
}

/** @param {string} bookingPageId @param {'UPCOMING' | 'PAST' | 'CANCELED'} scope @returns {Promise<BookingRow[]>} */
export async function listPageBookings(bookingPageId, scope) {
  return readData(await api.get(`/rest/booking-pages/${bookingPageId}/bookings`, { scope }), 'bookings') ?? [];
}

/** @param {string} bookingPageId @param {string} bookingId */
export function cancelPageBooking(bookingPageId, bookingId) {
  return api.post(`/rest/booking-pages/${bookingPageId}/bookings/${bookingId}/cancel`, {});
}
